/* eslint-disable prettier/prettier */

// ─── Common HTML → Structured Segments processor (processNode) ─────────────
// Used by both ExcelServiceForRFP.tsx (Excel) and PrintProposalSideBySide.tsx (PDF & Excel)

// Common formatted segment - intermediate representation from processNode
export interface FormattedSegment {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  fontSize?: number;
  color?: string;
}

// ExcelJS rich text item
export type RichTextItem = { text: string; font?: Record<string, any> };

// Strip HTML tags from a string to get plain text
export const stripHtml = (html: string): string => {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
};

// Convert color string to RGB tuple
export const colorToRgb = (color: string): [number, number, number] => {
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const full =
      hex.length === 3
        ? hex
            .split('')
            .map((c) => c + c)
            .join('')
        : hex;
    return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)];
  }
  const rgbMatch = color.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgbMatch) {
    return [parseInt(rgbMatch[1]), parseInt(rgbMatch[2]), parseInt(rgbMatch[3])];
  }
  const namedColors: Record<string, [number, number, number]> = {
    red: [255, 0, 0],
    blue: [0, 0, 255],
    green: [0, 128, 0],
    black: [0, 0, 0],
    white: [255, 255, 255],
    orange: [255, 165, 0],
    purple: [128, 0, 128],
    yellow: [255, 255, 0]
  };
  return namedColors[color.toLowerCase()] || [0, 0, 0];
};

// Convert RGB to ARGB hex string for ExcelJS
const rgbToArgb = (r: number, g: number, b: number): string => {
  return `FF${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// Merge consecutive segments with identical formatting and trim trailing newlines
const mergeSegments = (segments: FormattedSegment[]): FormattedSegment[] => {
  const merged: FormattedSegment[] = [];
  for (const seg of segments) {
    if (!seg.text) continue;
    const last = merged[merged.length - 1];
    if (
      last &&
      last.bold === seg.bold &&
      last.italic === seg.italic &&
      last.underline === seg.underline &&
      last.strike === seg.strike &&
      last.fontSize === seg.fontSize &&
      last.color === seg.color
    ) {
      last.text += seg.text;
    } else {
      merged.push({ ...seg });
    }
  }
  // Trim trailing newlines
  if (merged.length > 0) {
    merged[merged.length - 1].text = merged[merged.length - 1].text.replace(/\n+$/, '');
  }
  return merged.filter((s) => s.text !== '');
};

// ─── Core processNode: HTML → FormattedSegment[] ───────────────────────────

export const parseHtmlToSegments = (html: string): FormattedSegment[] => {
  if (!html || typeof html !== 'string') return [];
  const trimmed = html.trim();
  if (!trimmed) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(trimmed, 'text/html');
  const segments: FormattedSegment[] = [];

  const processNode = (
    node: Node,
    style: { bold?: boolean; italic?: boolean; underline?: boolean; strike?: boolean; fontSize?: number; color?: string },
    listCtx?: { type: 'ul' | 'ol'; index: number }
  ) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (text) segments.push({ text, ...style });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();
    const s = { ...style };
    let suffix = '';
    let childListCtx = listCtx;

    // Detect inline styles
    const inlineStyle = el.getAttribute('style') || '';
    const colorMatch = inlineStyle.match(/color\s*:\s*([^;]+)/i);
    const fontSizeMatch = inlineStyle.match(/font-size\s*:\s*(\d+)/i);
    if (colorMatch) s.color = colorMatch[1].trim();
    if (fontSizeMatch) s.fontSize = Number(fontSizeMatch[1]);

    switch (tag) {
      case 'strong':
      case 'b':
        s.bold = true;
        break;
      case 'em':
      case 'i':
        s.italic = true;
        break;
      case 'u':
        s.underline = true;
        break;
      case 's':
      case 'strike':
      case 'del':
        s.strike = true;
        break;
      case 'mark': {
        const bgColor = inlineStyle.match(/background-color\s*:\s*([^;]+)/i) || el.getAttribute('data-color');
        const highlightColor = Array.isArray(bgColor) ? bgColor[1]?.trim() : bgColor;
        if (highlightColor) s.color = highlightColor;
        else s.color = '#FFFF00'; // Default yellow highlight
        break;
      }
      case 'a': {
        const href = el.getAttribute('href');
        if (href) s.color = '#0000FF';
        break;
      }
      case 'h1':
        s.bold = true;
        s.fontSize = 16;
        suffix = '\n';
        break;
      case 'h2':
        s.bold = true;
        s.fontSize = 14;
        suffix = '\n';
        break;
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6':
        s.bold = true;
        s.fontSize = 12;
        suffix = '\n';
        break;
      case 'p':
      case 'div':
        suffix = '\n';
        break;
      case 'br':
        segments.push({ text: '\n', ...s });
        return;
      case 'ul':
        childListCtx = { type: 'ul', index: 0 };
        break;
      case 'ol':
        childListCtx = { type: 'ol', index: 0 };
        break;
      case 'li': {
        if (listCtx?.type === 'ul') {
          segments.push({ text: '• ', ...s });
        } else if (listCtx) {
          listCtx.index += 1;
          segments.push({ text: `${listCtx.index}. `, ...s });
        }
        suffix = '\n';
        break;
      }
      case 'blockquote':
        segments.push({ text: '  "', ...s });
        el.childNodes.forEach((child) => processNode(child, s, childListCtx));
        segments.push({ text: '"\n', ...s });
        return;
      default:
        break;
    }

    el.childNodes.forEach((child) => processNode(child, s, childListCtx));
    if (suffix) segments.push({ text: suffix, ...s });
  };

  doc.body.childNodes.forEach((node) => processNode(node, {}));

  return mergeSegments(segments);
};

// ─── Excel converter: FormattedSegment[] → RichTextItem[] ──────────────────

export const segmentsToExcelRichText = (segments: FormattedSegment[], colorArgb = 'FF000000'): RichTextItem[] => {
  const richText: RichTextItem[] = [];
  for (const seg of segments) {
    if (!seg.text) continue;
    const font: Record<string, any> = {
      size: seg.fontSize || 11,
      color: { argb: seg.color ? rgbToArgb(...colorToRgb(seg.color)) : colorArgb }
    };
    if (seg.bold) font.bold = true;
    if (seg.italic) font.italic = true;
    if (seg.underline) font.underline = true;
    if (seg.strike) font.strike = true;
    richText.push({ text: seg.text, font });
  }

  if (richText.length > 0) {
    richText[richText.length - 1].text = richText[richText.length - 1].text.replace(/\n+$/, '');
  }
  return richText.filter((p) => p.text !== '');
};

// ─── Convenience: HTML → ExcelJS rich text (combines parse + convert) ──────

export const htmlToExcelRichText = (html: string, colorArgb = 'FF000000'): RichTextItem[] => {
  const segments = parseHtmlToSegments(html);
  if (segments.length === 0) return [];
  return segmentsToExcelRichText(segments, colorArgb);
};

// ─── Inverse: ExcelJS rich text → HTML ─────────────────────────────────────
// Reconstructs HTML (bold/italic/underline/strike/colour) from the rich-text
// objects ExcelJS produces, so imported comments render their formatting in the
// rich-text editor instead of arriving as flat text. Block structure was
// flattened to newlines on export, so newlines become <br/>.

const escapeHtmlText = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const excelRichTextToHtml = (richText: RichTextItem[]): string => {
  if (!Array.isArray(richText)) return '';
  const inner = richText
    .map((item) => {
      if (!item || !item.text) return '';
      let text = escapeHtmlText(item.text).replace(/\n/g, '<br/>');
      const font = item.font || {};
      const argb: string | undefined = font.color?.argb;
      if (typeof argb === 'string' && /^FF[0-9A-Fa-f]{6}$/.test(argb) && argb.toUpperCase() !== 'FF000000') {
        text = `<span style="color:#${argb.slice(2)}">${text}</span>`;
      }
      if (font.bold) text = `<strong>${text}</strong>`;
      if (font.italic) text = `<em>${text}</em>`;
      if (font.underline) text = `<u>${text}</u>`;
      if (font.strike) text = `<s>${text}</s>`;
      return text;
    })
    .join('');
  return inner ? `<p>${inner}</p>` : '';
};

// ─── PDF converter: FormattedSegment[] → marker-based text string ──────────
// Used by PrintProposalSideBySide for PDF rendering via jsPDF

const MK_START = '\u00ab\u00ab';
const MK_END = '\u00bb\u00bb';

const PDF_MARKERS = {
  BOLD_START: `${MK_START}B${MK_END}`,
  BOLD_END: `${MK_START}/B${MK_END}`,
  ITALIC_START: `${MK_START}I${MK_END}`,
  ITALIC_END: `${MK_START}/I${MK_END}`,
  COLOR_START: (c: string) => `${MK_START}C:${c}${MK_END}`,
  COLOR_END: `${MK_START}/C${MK_END}`,
  SIZE_START: (s: number) => `${MK_START}S:${s}${MK_END}`,
  SIZE_END: `${MK_START}/S${MK_END}`
};

export const segmentsToPdfMarkedText = (segments: FormattedSegment[]): string => {
  return segments
    .map((seg) => {
      let { text } = seg;
      if (seg.bold) text = `${PDF_MARKERS.BOLD_START}${text}${PDF_MARKERS.BOLD_END}`;
      if (seg.italic) text = `${PDF_MARKERS.ITALIC_START}${text}${PDF_MARKERS.ITALIC_END}`;
      if (seg.color) text = `${PDF_MARKERS.COLOR_START(seg.color)}${text}${PDF_MARKERS.COLOR_END}`;
      if (seg.fontSize) text = `${PDF_MARKERS.SIZE_START(seg.fontSize)}${text}${PDF_MARKERS.SIZE_END}`;
      return text;
    })
    .join('');
};

// ─── Convenience: HTML → PDF marked text (combines parse + convert) ────────

export const htmlToPdfMarkedText = (html: string): string => {
  const segments = parseHtmlToSegments(html);
  if (segments.length === 0) return '';
  return segmentsToPdfMarkedText(segments);
};
